<template><markdown-wrapper><h1 id="pagination">Pagination <a class="header-anchor" href="#pagination">&#128279;</a></h1>
<blockquote>
<p>Provide pagination links for your site or app with the multi-page pagination component.</p>
</blockquote>
<p><ins class="adsbygoogle"
     style="display:block; text-align:center;"
     data-ad-layout="in-article"
     data-ad-format="fluid"
     data-ad-client="ca-pub-4714899946256166"
     data-ad-slot="4603582855"></ins></p>
<h2 id="example">Example <a class="header-anchor" href="#example">&#128279;</a></h2>
<p>Simple pagination, great for apps and search results.</p>
<p>Use <code v-pre>v-model</code> (1-based) to indicate the current page.</p>
<pagination-example ref="pagination-example"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">totalPage</span>: <span class="hljs-number">18</span>,
        <span class="hljs-attr">currentPage</span>: <span class="hljs-number">1</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- pagination-example.vue --&gt;</span>
</code></pre>
<h2 id="sizing">Sizing <a class="header-anchor" href="#sizing">&#128279;</a></h2>
<p>Fancy larger or smaller pagination? Add <code v-pre>size=&quot;lg&quot;</code> or <code v-pre>size=&quot;sm&quot;</code> for additional sizes.</p>
<pagination-sizing ref="pagination-sizing"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span> <span class="hljs-attr">size</span>=<span class="hljs-string">&quot;lg&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span> <span class="hljs-attr">size</span>=<span class="hljs-string">&quot;sm&quot;</span>/&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">totalPage</span>: <span class="hljs-number">18</span>,
        <span class="hljs-attr">currentPage</span>: <span class="hljs-number">1</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- pagination-sizing.vue --&gt;</span>
</code></pre>
<h2 id="alignment">Alignment <a class="header-anchor" href="#alignment">&#128279;</a></h2>
<p>By default the pagination component is left aligned. Change the alignment to center or right by setting <code v-pre>align</code> to the appropriate value.</p>
<pagination-alignment ref="pagination-alignment"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span> <span class="hljs-attr">align</span>=<span class="hljs-string">&quot;center&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span> <span class="hljs-attr">align</span>=<span class="hljs-string">&quot;right&quot;</span>/&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">totalPage</span>: <span class="hljs-number">18</span>,
        <span class="hljs-attr">currentPage</span>: <span class="hljs-number">1</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- pagination-alignment.vue --&gt;</span>
</code></pre>
<h2 id="direction-links">Direction links <a class="header-anchor" href="#direction-links">&#128279;</a></h2>
<p>By default <code v-pre>direction-links</code> are enabled, which allows users to nav to previous or next page.</p>
<pagination-direction-links ref="pagination-direction-links"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span> <span class="hljs-attr">:direction-links</span>=<span class="hljs-string">&quot;false&quot;</span>/&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">totalPage</span>: <span class="hljs-number">18</span>,
        <span class="hljs-attr">currentPage</span>: <span class="hljs-number">1</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- pagination-direction-links.vue --&gt;</span>
</code></pre>
<h2 id="boundary-links">Boundary links <a class="header-anchor" href="#boundary-links">&#128279;</a></h2>
<p>Add <code v-pre>boundary-links</code> to allow fast nav to the first or last page.</p>
<pagination-boundary-links ref="pagination-boundary-links"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span> <span class="hljs-attr">boundary-links</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">totalPage</span>: <span class="hljs-number">18</span>,
        <span class="hljs-attr">currentPage</span>: <span class="hljs-number">1</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- pagination-boundary-links.vue --&gt;</span>
</code></pre>
<h2 id="chunks">Chunks <a class="header-anchor" href="#chunks">&#128279;</a></h2>
<p>Use <code v-pre>max-size</code> to define the maximum chunk size of pagers (default is 5). And if you don't like chunk pagers, just simply set <code v-pre>max-size</code> to equal as <code v-pre>total-page</code>.</p>
<pagination-chunks ref="pagination-chunks"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span> <span class="hljs-attr">:max-size</span>=<span class="hljs-string">&quot;3&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span> <span class="hljs-attr">:max-size</span>=<span class="hljs-string">&quot;totalPage&quot;</span>/&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">totalPage</span>: <span class="hljs-number">10</span>,
        <span class="hljs-attr">currentPage</span>: <span class="hljs-number">1</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- pagination-chunks.vue --&gt;</span>
</code></pre>
<h2 id="disabled">Disabled <a class="header-anchor" href="#disabled">&#128279;</a></h2>
<pagination-disabled ref="pagination-disabled"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">pagination</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;currentPage&quot;</span> <span class="hljs-attr">:total-page</span>=<span class="hljs-string">&quot;totalPage&quot;</span> <span class="hljs-attr">disabled</span>/&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">totalPage</span>: <span class="hljs-number">18</span>,
        <span class="hljs-attr">currentPage</span>: <span class="hljs-number">1</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- pagination-disabled.vue --&gt;</span>
</code></pre>
<h1 id="api-reference">API Reference <a class="header-anchor" href="#api-reference">&#128279;</a></h1>
<h2 id="pagination-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/components/pagination/Pagination.vue">Pagination</a> <a class="header-anchor" href="#pagination-1">&#128279;</a></h2>
<h3 id="props">Props <a class="header-anchor" href="#props">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>v-model</code></td>
<td>Number</td>
<td></td>
<td>✔</td>
<td>The current page (1-based).</td>
</tr>
<tr>
<td><code v-pre>total-page</code></td>
<td>Number</td>
<td></td>
<td>✔</td>
<td>Total number of pages.</td>
</tr>
<tr>
<td><code v-pre>max-size</code></td>
<td>Number</td>
<td>5</td>
<td></td>
<td>Maximum number of pagers per chunk.</td>
</tr>
<tr>
<td><code v-pre>boundary-links</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Display First / Last buttons.</td>
</tr>
<tr>
<td><code v-pre>direction-links</code></td>
<td>Boolean</td>
<td>true</td>
<td></td>
<td>Display Previous / Next buttons.</td>
</tr>
<tr>
<td><code v-pre>size</code></td>
<td>String</td>
<td></td>
<td></td>
<td>Optional pagination sizes, support: <code v-pre>sm</code> / <code v-pre>lg</code></td>
</tr>
<tr>
<td><code v-pre>align</code></td>
<td>String</td>
<td></td>
<td></td>
<td>Optional pagination alignment, support: <code v-pre>left</code> / <code v-pre>center</code> / <code v-pre>right</code></td>
</tr>
<tr>
<td><code v-pre>disabled</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Disable the pagination component.</td>
</tr>
</tbody>
</table></div><h3 id="events">Events <a class="header-anchor" href="#events">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Params</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>change</code></td>
<td>index</td>
<td>Fire after page changed, with the index number changed to.</td>
</tr>
</tbody>
</table></div></markdown-wrapper></template><script>
  

  

  

  

  

  

  
export default {components:{'pagination-example':{"template":"<div class=\"markdown-live-example\">\n  <pagination v-model=\"currentPage\" :total-page=\"totalPage\"/>\n</div>",
    data () {
      return {
        totalPage: 18,
        currentPage: 1
      }
    }
  },'pagination-sizing':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\" size=\"lg\"/>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\"/>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\" size=\"sm\"/>\n  </section>\n</div>",
    data () {
      return {
        totalPage: 18,
        currentPage: 1
      }
    }
  },'pagination-alignment':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\"/>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\" align=\"center\"/>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\" align=\"right\"/>\n  </section>\n</div>",
    data () {
      return {
        totalPage: 18,
        currentPage: 1
      }
    }
  },'pagination-direction-links':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\"/>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\" :direction-links=\"false\"/>\n  </section>\n</div>",
    data () {
      return {
        totalPage: 18,
        currentPage: 1
      }
    }
  },'pagination-boundary-links':{"template":"<div class=\"markdown-live-example\">\n  <pagination v-model=\"currentPage\" :total-page=\"totalPage\" boundary-links/>\n</div>",
    data () {
      return {
        totalPage: 18,
        currentPage: 1
      }
    }
  },'pagination-chunks':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\" :max-size=\"3\"/>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\"/>\n    <pagination v-model=\"currentPage\" :total-page=\"totalPage\" :max-size=\"totalPage\"/>\n  </section>\n</div>",
    data () {
      return {
        totalPage: 10,
        currentPage: 1
      }
    }
  },'pagination-disabled':{"template":"<div class=\"markdown-live-example\">\n  <pagination v-model=\"currentPage\" :total-page=\"totalPage\" disabled/>\n</div>",
    data () {
      return {
        totalPage: 18,
        currentPage: 1
      }
    }
  }}}</script>